/**
 * Quiz "Crie sua fórmula"
 *
 * Passos: tipo de cabelo -> textura -> objetivos (multi) -> fragrância -> formato -> Finalizar.
 * Passo sem nenhuma opção (ex.: formato num quiz sem blocos "Formato") sai do quiz; o último que
 * sobra vira o passo final. Formato "REF" (refil): shampoo e condicionador vêm das linhas REF.* do
 * mapa de variantes (ex.: REF.SH.L.B); o que não tiver linha REF (máscara, leave-in) segue o normal.
 *
 * Estrutura no DOM (ver blocks/quiz-formula.liquid):
 *   - <h3 data-quiz-step="chave" ...>  define cada passo (ordem = ordem do quiz)
 *   - [data-quiz-option][data-quiz-kind="chave"]  opções (blocos filhos), todas numa lista única;
 *     apenas as do passo atual ficam visíveis.
 *
 * Ao finalizar:
 *   0. se o carrinho já tem uma fórmula do quiz (itens com a propriedade oculta "_formula"), pergunta
 *      "trocar pela nova" (remove só esses itens) ou "levar as duas" (o pedido registra as duas);
 *   1. grava as respostas como *cart attributes* (campos personalizados do pedido, independentes da nota);
 *   2. adiciona os produtos da fórmula ao carrinho, marcados com "_formula" e "_quiz";
 *   3. salva as respostas no localStorage (aba "Recuperar minha fórmula");
 *   4. redireciona para /cart.
 *
 * Visual V21 (data-design="v21", template product.quiz): não avança sozinho; a cliente escolhe e
 * clica em Continuar (desabilitado até responder). Mostra contador, orientação da pergunta e o atalho
 * "Recuperar minhas escolhas" no 1º passo quando há fórmula salva.
 */

const STORAGE_KEY = 'narte:quiz-formula';
const DEFAULT_PRODUCT_TYPES = ['SH', 'CO', 'MS', 'LI'];

class QuizFormula extends HTMLElement {
  /** @type {HTMLElement[]} */
  #steps = [];
  #stepIndex = 0;
  /** @type {Record<string, any>} */
  #answers = {};
  #busy = false;
  #advanceTimer = 0;
  /** Botão Finalizar que abriu a pergunta "trocar ou levar as duas". */
  #replaceTrigger = null;
  #v21 = false;

  connectedCallback() {
    this.#v21 = this.dataset.design === 'v21';
    // Só valem os passos que têm opções; o último deles é o que mostra "Finalizar".
    this.#steps = Array.from(this.querySelectorAll('[data-quiz-step]')).filter(
      (step) => this.#options(step.dataset.quizStep).length > 0
    );
    this.#steps.forEach((step, i) => {
      if (i === this.#steps.length - 1) step.dataset.quizFinal = 'true';
      else delete step.dataset.quizFinal;
    });
    // Passo sem título no tema (formato): pergunta e orientação vêm da 1ª opção dele.
    this.#steps.forEach((step) => {
      if (step.textContent.trim()) return;
      const key = step.dataset.quizStep;
      const first = this.#options(key)[0];
      step.textContent = first.dataset.quizQuestion || '';
      const help = this.querySelector(`[data-quiz-help="${key}"]`);
      if (help && !help.textContent.trim()) help.textContent = first.dataset.quizHelpText || '';
    });
    const progress = this.querySelector('[data-quiz-progress]');
    if (progress && progress.children.length !== this.#steps.length) {
      progress.replaceChildren(...this.#steps.map(() => document.createElement('span')));
    }
    this.addEventListener('click', this.#onClick);
    document.addEventListener('shopify:block:select', this.#onEditorSelect);

    this.#renderRecover();
    this.#goTo(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
    clearTimeout(this.#advanceTimer);
  }

  /* ------------------------------------------------------------------ */
  /* Eventos                                                             */
  /* ------------------------------------------------------------------ */

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const tab = target.closest('[data-quiz-tab]');
    if (tab) return this.#showPanel(tab.dataset.quizTab);

    if (target.closest('[data-quiz-back]')) {
      this.#goTo(this.#stepIndex - 1);
      this.#revealTop();
      return;
    }

    const option = target.closest('[data-quiz-option]');
    if (option && this.contains(option)) return this.#select(option);

    if (target.closest('[data-quiz-continue]')) return this.#continue();
    const finish = target.closest('[data-quiz-finish]');
    if (finish) return this.#finish(finish);
    const choice = target.closest('[data-quiz-replace-choice]');
    if (choice) return this.#finish(this.#replaceTrigger, choice.dataset.quizReplaceChoice);
    // Pop-up: "×" ou toque no fundo escurecido (o clique cai no próprio <dialog>) fecha sem mudar nada.
    if (target.closest('[data-quiz-replace-close]')) return this.#hideReplace();
    if (target.matches('[data-quiz-replace]')) {
      const r = target.getBoundingClientRect();
      const outside = event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
      if (outside) this.#hideReplace();
      return;
    }
    if (target.closest('[data-quiz-redo]')) return this.#redo();
  };

  /** No editor de temas, ao clicar em uma opção, mostra o passo correspondente. */
  #onEditorSelect = (event) => {
    const el = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(el)) return;
    const option = el.closest('[data-quiz-option]');
    if (!option) return;
    const index = this.#steps.findIndex((s) => s.dataset.quizStep === option.dataset.quizKind);
    if (index < 0) return;
    this.#showPanel('create');
    this.#goTo(index);
  };

  /* ------------------------------------------------------------------ */
  /* Navegação                                                           */
  /* ------------------------------------------------------------------ */

  #showPanel(name) {
    this.#hideReplace();
    this.querySelectorAll('[data-quiz-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.quizPanel !== name;
    });
    this.querySelectorAll('[data-quiz-tab]').forEach((tab) => {
      const active = tab.dataset.quizTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    if (name === 'recover') this.#renderRecover();
  }

  #goTo(index) {
    clearTimeout(this.#advanceTimer);
    this.#hideReplace();
    if (index < 0 || index >= this.#steps.length) return;
    this.#stepIndex = index;

    const step = this.#steps[index];
    const key = step.dataset.quizStep;

    // Título do passo
    this.#steps.forEach((s, i) => {
      s.hidden = i !== index;
    });

    // Opções: só as do passo atual
    this.#options().forEach((o) => {
      o.hidden = o.dataset.quizKind !== key;
    });

    // Layout da lista (grade para múltipla escolha)
    const list = this.querySelector('[data-quiz-options]');
    if (list) list.classList.toggle('quiz-formula__options--grid', step.dataset.quizLayout === 'grid');

    // Botões
    const back = this.querySelector('[data-quiz-back]');
    if (back) back.hidden = index === 0;

    const cont = this.querySelector('[data-quiz-panel="create"] [data-quiz-continue]');
    if (cont) cont.hidden = step.dataset.quizMulti !== 'true';

    const finish = this.querySelector('[data-quiz-panel="create"] [data-quiz-finish]');
    if (finish) finish.hidden = !(step.dataset.quizFinal === 'true' && this.#answers[key]);

    // Progresso
    this.querySelectorAll('[data-quiz-progress] span').forEach((seg, i) => {
      seg.classList.toggle('is-filled', i <= index);
    });

    if (this.#v21) {
      const pad = (n) => String(n).padStart(2, '0');
      const counter = this.querySelector('[data-quiz-counter]');
      if (counter) counter.textContent = `${pad(index + 1)} / ${pad(this.#steps.length)}`;
      const name = this.querySelector('[data-quiz-step-name]');
      if (name) name.textContent = step.dataset.quizLabel || '';
      this.querySelectorAll('[data-quiz-help]').forEach((help) => {
        help.hidden = help.dataset.quizHelp !== key;
      });
      const entry = this.querySelector('[data-quiz-recover-entry]');
      if (entry) entry.hidden = !(index === 0 && this.#load());
      this.#refreshActions();
    }

    this.#setError('');
  }

  /** V21: Continuar/Finalizar só habilitam quando o passo atual está respondido. */
  #refreshActions() {
    if (!this.#v21) return;
    const step = this.#currentStep;
    if (!step) return;
    const final = step.dataset.quizFinal === 'true';
    const answered = this.#isAnswered(step);
    const panel = this.querySelector('[data-quiz-panel="create"]');
    const cont = /** @type {HTMLButtonElement | null} */ (panel && panel.querySelector('[data-quiz-continue]'));
    const finish = /** @type {HTMLButtonElement | null} */ (panel && panel.querySelector('[data-quiz-finish]'));
    if (cont) {
      cont.hidden = final;
      cont.disabled = !answered;
    }
    if (finish) {
      finish.hidden = !final;
      finish.disabled = !answered;
    }
  }

  #isAnswered(step) {
    const answer = this.#answers[step.dataset.quizStep];
    const min = Number(step.dataset.quizMin || 1);
    return Array.isArray(answer) ? answer.length >= min : Boolean(answer);
  }

  /** V21: ao trocar de passo, traz o topo do quiz de volta à tela se ele ficou acima. */
  #revealTop() {
    if (!this.#v21) return;
    if (this.getBoundingClientRect().top >= 0) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ block: 'start', behavior: reduced ? 'instant' : 'smooth' });
  }

  get #currentStep() {
    return this.#steps[this.#stepIndex];
  }

  /** Opções do passo informado (ou todas). */
  #options(key) {
    const all = Array.from(this.querySelectorAll('[data-quiz-option]'));
    return key ? all.filter((o) => o.dataset.quizKind === key) : all;
  }

  /* ------------------------------------------------------------------ */
  /* Seleção                                                             */
  /* ------------------------------------------------------------------ */

  #select(option) {
    const step = this.#currentStep;
    const key = step && step.dataset.quizStep;
    if (!key || option.dataset.quizKind !== key) return;

    const multi = step.dataset.quizMulti === 'true';
    const options = this.#options(key);

    if (multi) {
      const max = Number(step.dataset.quizMax || 0);
      const selectedCount = options.filter((o) => o.classList.contains('is-selected')).length;
      const selecting = !option.classList.contains('is-selected');

      if (selecting && max > 0 && selectedCount >= max) {
        this.#setError((this.dataset.maxMessage || 'Você pode escolher até [n] objetivos.').replace('[n]', String(max)));
        return;
      }

      option.classList.toggle('is-selected');
      option.setAttribute('aria-pressed', option.classList.contains('is-selected') ? 'true' : 'false');
      const selected = options.filter((o) => o.classList.contains('is-selected'));
      this.#answers[key] = selected.map((o) => this.#optionData(o));

      // Ao atingir o máximo, as demais opções ficam visualmente bloqueadas.
      const full = max > 0 && selected.length >= max;
      options.forEach((o) => o.classList.toggle('is-disabled', full && !o.classList.contains('is-selected')));

      this.#setError('');
      this.#refreshActions();
      return;
    }

    options.forEach((o) => {
      o.classList.toggle('is-selected', o === option);
      o.setAttribute('aria-pressed', o === option ? 'true' : 'false');
    });
    this.#answers[key] = this.#optionData(option);

    if (this.#v21) {
      this.#refreshActions();
      return;
    }

    if (step.dataset.quizFinal === 'true') {
      // Último passo: revela o botão "Finalizar" em vez de avançar.
      const finish = this.querySelector('[data-quiz-panel="create"] [data-quiz-finish]');
      if (finish) finish.hidden = false;
      return;
    }

    this.#advanceTimer = window.setTimeout(() => this.#goTo(this.#stepIndex + 1), 220);
  }

  #optionData(option) {
    return {
      value: option.dataset.quizValue || '',
      code: option.dataset.quizCode || '',
      detail: option.dataset.quizDetail || '',
      altCode: option.dataset.quizAltCode || '',
      altTrigger: option.dataset.quizAltTrigger || '',
    };
  }

  /**
   * Código de fórmula do cabelo. Se o tipo tiver uma fórmula alternativa e o objetivo
   * que a ativa estiver entre os escolhidos (ex.: Ondulado + "Definição de cachos" -> CH),
   * usa a alternativa.
   */
  #resolveHairCode(answers) {
    const hair = answers.hair_type || {};
    const base = (hair.code || '').toUpperCase();
    const alt = (hair.altCode || '').toUpperCase();
    const trigger = (hair.altTrigger || '').trim().toLowerCase();
    if (!alt || !trigger) return base;

    const goals = Array.isArray(answers.goals) ? answers.goals : [];
    const hit = goals.some((g) => (g.value || '').trim().toLowerCase() === trigger);
    return hit ? alt : base;
  }

  #continue() {
    const step = this.#currentStep;
    const key = step.dataset.quizStep;
    const answer = this.#answers[key];
    const min = Number(step.dataset.quizMin || 1);

    if (!answer || (Array.isArray(answer) && answer.length < min)) {
      this.#setError(this.dataset.requiredMessage || 'Selecione ao menos uma opção.');
      return;
    }
    this.#goTo(this.#stepIndex + 1);
    this.#revealTop();
  }

  /* ------------------------------------------------------------------ */
  /* Finalizar                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * @param {HTMLElement | null} trigger botão Finalizar clicado (para posicionar a pergunta)
   * @param {string} [choice] 'replace' | 'keep': resposta à pergunta, quando já há fórmula no carrinho
   */
  async #finish(trigger = null, choice) {
    if (this.#busy) return;

    const answers = this.#collectAnswers();
    if (!answers) return;

    // "format" pode faltar em fórmula salva antes da pergunta existir: vale como frasco tradicional.
    const missing = this.#steps
      .map((s) => s.dataset.quizStep)
      .filter((k) => k !== 'format' || this.#panelIsCreate())
      .filter((k) => !answers[k] || (Array.isArray(answers[k]) && !answers[k].length));
    if (missing.length) {
      this.#setError(this.dataset.incompleteMessage || 'Responda todas as perguntas para finalizar.');
      return;
    }

    const hair = this.#resolveHairCode(answers);
    const fragrance = (answers.fragrance.code || '').toUpperCase();
    const map = this.#variantMap();
    const items = [];
    const notFound = [];

    // Cada produto da fórmula leva propriedades ocultas (com "_", a Shopify não as mostra no checkout):
    // "_formula" identifica a fórmula; "_quiz" separa cada finalização, para nunca somar na linha de outra.
    const properties = { _formula: `${hair}.${fragrance}`, _quiz: Date.now().toString(36) };

    // Refil: tenta antes a linha REF.* (só shampoo e condicionador têm); sem ela, segue o normal.
    const refill = ((answers.format && answers.format.code) || '').toUpperCase() === 'REF';

    // Para cada produto da fórmula, procura no mapa do mais específico ao mais genérico:
    // SH.L.P -> SH.L -> SH (ex.: leave-in só varia por cabelo: LI.L)
    for (const type of this.#productTypes()) {
      const normal = [`${type}.${hair}.${fragrance}`, `${type}.${hair}`, type];
      const candidates = refill ? [`REF.${type}.${hair}.${fragrance}`, `REF.${type}.${hair}`, ...normal] : normal;
      const key = candidates.find((k) => map[k]);
      if (key) items.push({ id: Number(map[key]), quantity: 1, properties, type });
      else notFound.push(normal[0]);
    }

    if (notFound.length) {
      this.#setError(`${this.dataset.mapErrorMessage || 'Fórmula não encontrada:'} ${notFound.join(', ')}`);
      return;
    }

    let attributes = this.#buildAttributes(answers, hair, fragrance);
    const finishButtons = this.querySelectorAll('[data-quiz-finish]');

    this.#busy = true;
    finishButtons.forEach((b) => {
      b.setAttribute('aria-disabled', 'true');
      b.classList.add('is-loading');
    });

    try {
      // 0) Já existe fórmula do quiz no carrinho? Pergunta antes: trocar pela nova ou levar as duas.
      //    Conta como fórmula o que tem "_formula" e, em carrinho antigo, os 4 produtos personalizados;
      //    extras (sérum, óleo, perfume...) e brindes nunca são tocados.
      const cart = await this.#getCart();
      const previous = (cart.items || []).filter((item) => this.#isFormulaItem(item));
      if (previous.length && choice !== 'replace' && choice !== 'keep') {
        this.#showReplace(trigger);
        return;
      }
      this.#hideReplace();

      if (previous.length && choice === 'replace') {
        // Tira a fórmula anterior antes de pôr a nova: se a adição falhar, um novo "Finalizar" não duplica.
        await this.#removeFormulaItems(previous);
      } else if (previous.length && choice === 'keep') {
        attributes = this.#mergeAttributes(cart.attributes || {}, attributes);
      }

      // 1) Campos personalizados do pedido (cart attributes -> "Detalhes adicionais" no admin).
      await this.#post(this.dataset.updateUrl, { attributes });

      // 2) Shampoo + condicionador + máscara da fórmula.
      const toAdd = items.map(({ type, ...item }) => item);
      try {
        const added = await this.#post(this.dataset.addUrl, { items: toAdd });
        if (added && added.status) throw new Error(added.description || added.message || 'Erro ao adicionar ao carrinho');
      } catch (addError) {
        // Um item esgotado faz a Shopify recusar o pedido inteiro (A06). Tenta um a um e segue
        // com o que entrou; a tela seguinte avisa qual produto ficou de fora.
        const missing = [];
        for (const item of items) {
          const { type, ...one } = item;
          try {
            await this.#post(this.dataset.addUrl, { items: [one] });
          } catch (e) {
            missing.push(type);
          }
        }
        if (missing.length === items.length) throw addError;
        try {
          sessionStorage.setItem('narte:quiz-missing', JSON.stringify(missing));
        } catch (e) {}
      }

      // 3) Guarda para "Recuperar minha fórmula".
      this.#save({ answers, attributes, items: items.map(({ type, ...item }) => item), savedAt: new Date().toISOString() });

      // Fórmula nova: esquece os produtos que a cliente tinha tirado da fórmula anterior.
      try {
        sessionStorage.removeItem('narte:routine-removed');
      } catch (e) {}

      // 4) Vai para o carrinho.
      window.location.href = this.dataset.redirectUrl || this.dataset.cartUrl || '/cart';
    } catch (error) {
      this.#setError(this.dataset.cartErrorMessage || 'Não foi possível adicionar os produtos. Tente novamente.');
      console.error('[quiz-formula]', error);
    } finally {
      this.#busy = false;
      finishButtons.forEach((b) => {
        b.removeAttribute('aria-disabled');
        b.classList.remove('is-loading');
      });
    }
  }

  async #getCart() {
    const response = await fetch(`${this.dataset.cartJsonUrl || '/cart.js'}?t=${Date.now()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  /**
   * Produto de fórmula já no carrinho: marcado pelo quiz ("_formula") ou, em carrinho antigo
   * (antes da marca existir ou vindo de outra rota), shampoo, condicionador, máscara ou leave-in
   * personalizado. Brinde nunca conta. Mesma leitura por nome usada em sections/quiz-routine.liquid.
   */
  #isFormulaItem(item) {
    const props = item.properties || {};
    if (props._gift || props.gift) return false;
    if (props._formula) return true;
    const title = String(item.product_title || item.title || '').toLowerCase();
    return (
      title.includes('shampoo personalizado') ||
      title.includes('condicionador personalizado') ||
      title.includes('máscara de tratamento personalizada') ||
      title.includes('mascara de tratamento personalizada') ||
      title.includes('leave-in')
    );
  }

  /**
   * "Trocar pela nova": remove os produtos da fórmula anterior e confere no carrinho.
   * Se algum ficar, tenta de novo linha a linha; se ainda assim ficar, para (não soma as duas).
   */
  async #removeFormulaItems(items) {
    const updates = {};
    items.forEach((item) => {
      updates[item.key] = 0;
    });
    try {
      await this.#post(this.dataset.updateUrl, { updates });
    } catch (e) {
      console.warn('[quiz-formula] remoção em lote falhou, tentando linha a linha', e);
    }

    let left = (await this.#getCart()).items.filter((item) => this.#isFormulaItem(item));
    for (const item of left) {
      try {
        await this.#post(this.dataset.changeUrl || '/cart/change.js', { id: item.key, quantity: 0 });
      } catch (e) {
        console.warn('[quiz-formula] não removeu', item.key, e);
      }
    }
    if (left.length) left = (await this.#getCart()).items.filter((item) => this.#isFormulaItem(item));
    if (left.length) throw new Error(`Fórmula anterior não saiu do carrinho (${left.length} itens)`);
  }

  /** Abre o pop-up de alerta "trocar pela nova ou levar as duas". */
  #showReplace(trigger) {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-quiz-replace]'));
    if (!dialog) return;
    this.#replaceTrigger = trigger;
    this.#setError('');
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    const first = dialog.querySelector('[data-quiz-replace-choice]');
    if (first) first.focus();
  }

  #hideReplace() {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-quiz-replace]'));
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  /**
   * "Levar as duas": o pedido registra as duas fórmulas no mesmo campo, separadas por " | "
   * (ex.: Fórmula "CH.P | L.B"). Valores iguais não se repetem.
   */
  #mergeAttributes(current, next) {
    const merged = {};
    Object.entries(next).forEach(([label, value]) => {
      const old = String(current[label] || '').trim();
      const parts = old ? old.split(' | ') : [];
      if (value && !parts.includes(String(value))) parts.push(String(value));
      merged[label] = parts.join(' | ');
    });
    return merged;
  }

  #panelIsCreate() {
    const recoverPanel = this.querySelector('[data-quiz-panel="recover"]');
    return !recoverPanel || recoverPanel.hidden;
  }

  /** Respostas da aba ativa: as atuais (Criar) ou as salvas (Recuperar). */
  #collectAnswers() {
    const recoverPanel = this.querySelector('[data-quiz-panel="recover"]');
    if (recoverPanel && !recoverPanel.hidden) {
      const saved = this.#load();
      return saved ? saved.answers : null;
    }
    return this.#answers;
  }

  #buildAttributes(answers, hair, fragrance) {
    const labels = {};
    this.#steps.forEach((s) => {
      labels[s.dataset.quizStep] = s.dataset.quizLabel || s.dataset.quizStep;
    });

    const goals = Array.isArray(answers.goals) ? answers.goals : [];
    const attributes = {
      [labels.hair_type]: answers.hair_type.value,
      [labels.texture]: answers.texture.value,
      [labels.goals]: goals.map((g) => g.value).join(', '),
      [labels.fragrance]: answers.fragrance.value,
      [this.dataset.formulaLabel || 'Fórmula']: `${hair}.${fragrance}`,
    };
    if (labels.format) {
      attributes[labels.format] = (answers.format && answers.format.value) || 'Frasco tradicional';
    }

    const actives = goals.map((g) => g.detail).filter(Boolean);
    if (actives.length && this.dataset.activesLabel) {
      attributes[this.dataset.activesLabel] = actives.join(', ');
    }
    return attributes;
  }

  /** Códigos dos produtos adicionados ao finalizar (setting "Produtos da fórmula"). */
  #productTypes() {
    const raw = (this.dataset.productTypes || '').split(/[,\s]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
    return raw.length ? raw : DEFAULT_PRODUCT_TYPES;
  }

  /** Lê o mapa "SH.L.F: 123456" do admin. Linhas iniciadas por # são ignoradas. */
  #variantMap() {
    const map = {};
    const raw = this.dataset.variantMap || '';
    raw.split(/[\r\n;]+/).forEach((line) => {
      const clean = line.trim();
      if (!clean || clean.startsWith('#')) return;
      const [key, value] = clean.split(/[:=]/).map((s) => (s || '').trim());
      if (key && /^\d+$/.test(value)) map[key.toUpperCase()] = value;
    });
    return map;
  }

  async #post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.description || json.message || response.statusText);
    return json;
  }

  /* ------------------------------------------------------------------ */
  /* Recuperar minha fórmula                                             */
  /* ------------------------------------------------------------------ */

  #renderRecover() {
    const empty = this.querySelector('[data-quiz-recover-empty]');
    const summary = this.querySelector('[data-quiz-recover-summary]');
    const list = this.querySelector('[data-quiz-recover-list]');
    if (!empty || !summary || !list) return;

    const saved = this.#load();
    if (!saved) {
      empty.hidden = false;
      summary.hidden = true;
      return;
    }

    list.innerHTML = '';
    Object.entries(saved.attributes || {}).forEach(([label, value]) => {
      if (!value) return;
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      list.append(dt, dd);
    });

    empty.hidden = true;
    summary.hidden = false;
  }

  #redo() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    this.#answers = {};
    this.#options().forEach((o) => {
      o.classList.remove('is-selected', 'is-disabled');
      o.setAttribute('aria-pressed', 'false');
    });
    this.#showPanel('create');
    this.#goTo(0);
  }

  #save(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  #load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  #setError(message) {
    const el = this.querySelector('[data-quiz-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }
}

if (!customElements.get('quiz-formula')) {
  customElements.define('quiz-formula', QuizFormula);
}
